import * as THREE from 'three';
import { LOOK_DEFAULTS, resolveLook } from './look.js';

/**
 * The picture after the scene is drawn (step 98): bloom, a colour grade and a
 * vignette, each arena's own.
 *
 * Written here rather than taken from three's examples. `EffectComposer` with
 * `UnrealBloomPass` is about 20 kB of script against a 35 kB margin, and its
 * bloom is five separable Gaussian blurs at up to half resolution. This is a
 * dual-filter bloom — a bright pass at half resolution, then each level a
 * quarter the size of the last, down and back up again, five taps a pixel —
 * and the grade and the vignette ride along in the one full-screen pass that
 * has to happen anyway to put the picture on the screen.
 *
 * The scene is drawn into a half-float target, linear and unclamped, so a
 * headlight at seven times white is still seven times white when the bright
 * pass reads it; with two samples of MSAA (`SAMPLES`), because a render
 * target does not get the canvas's antialiasing. The last pass encodes to sRGB, which the
 * renderer would otherwise have done drawing straight to the canvas.
 *
 * An arena with no `look.post` is drawn straight to the canvas as before.
 */

const fullscreen = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = position.xy * 0.5 + 0.5;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

// Bright pass, at half resolution: a four-tap average (so a single hot pixel
// does not flicker), then only what is over the threshold, with a soft knee.
const brightShader = /* glsl */ `
  uniform sampler2D source;
  uniform vec2 texel;
  uniform float threshold;
  uniform float knee;
  varying vec2 vUv;
  void main() {
    vec3 c = texture2D(source, vUv + texel * vec2(-0.5, -0.5)).rgb;
    c += texture2D(source, vUv + texel * vec2(0.5, -0.5)).rgb;
    c += texture2D(source, vUv + texel * vec2(-0.5, 0.5)).rgb;
    c += texture2D(source, vUv + texel * vec2(0.5, 0.5)).rgb;
    c *= 0.25;
    float bright = max(c.r, max(c.g, c.b));
    float soft = clamp(bright - threshold + knee, 0.0, 2.0 * knee);
    soft = soft * soft / (4.0 * knee + 1e-4);
    float keep = max(soft, bright - threshold) / max(bright, 1e-4);
    gl_FragColor = vec4(c * keep, 1.0);
  }
`;

// Dual-filter down: the centre and four diagonal neighbours.
const downShader = /* glsl */ `
  uniform sampler2D source;
  uniform vec2 texel;
  varying vec2 vUv;
  void main() {
    vec3 c = texture2D(source, vUv).rgb * 4.0;
    c += texture2D(source, vUv + texel * vec2(-1.0, -1.0)).rgb;
    c += texture2D(source, vUv + texel * vec2(1.0, -1.0)).rgb;
    c += texture2D(source, vUv + texel * vec2(-1.0, 1.0)).rgb;
    c += texture2D(source, vUv + texel * vec2(1.0, 1.0)).rgb;
    gl_FragColor = vec4(c / 8.0, 1.0);
  }
`;

// Dual-filter up: a tent of eight taps, added onto the level above.
const upShader = /* glsl */ `
  uniform sampler2D source;
  uniform vec2 texel;
  uniform float weight;
  varying vec2 vUv;
  void main() {
    vec3 c = texture2D(source, vUv + texel * vec2(-2.0, 0.0)).rgb;
    c += texture2D(source, vUv + texel * vec2(2.0, 0.0)).rgb;
    c += texture2D(source, vUv + texel * vec2(0.0, -2.0)).rgb;
    c += texture2D(source, vUv + texel * vec2(0.0, 2.0)).rgb;
    c += texture2D(source, vUv + texel * vec2(-1.0, -1.0)).rgb * 2.0;
    c += texture2D(source, vUv + texel * vec2(1.0, -1.0)).rgb * 2.0;
    c += texture2D(source, vUv + texel * vec2(-1.0, 1.0)).rgb * 2.0;
    c += texture2D(source, vUv + texel * vec2(1.0, 1.0)).rgb * 2.0;
    gl_FragColor = vec4(c / 12.0 * weight, 1.0);
  }
`;

// The picture: scene plus bloom, exposure, saturation, then into sRGB for the
// contrast, lift and gain a colourist would turn, and the vignette last.
const finalShader = /* glsl */ `
  uniform sampler2D scene;
  uniform sampler2D bloom;
  uniform float strength;
  uniform float exposure;
  uniform float saturation;
  uniform float contrast;
  uniform vec3 lift;
  uniform vec3 gain;
  uniform float vignette;
  uniform float softness;
  uniform float aspect;
  varying vec2 vUv;
  vec3 toSRGB(vec3 c) {
    c = clamp(c, 0.0, 1.0);
    return mix(c * 12.92, 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c));
  }
  void main() {
    vec3 c = texture2D(scene, vUv).rgb + texture2D(bloom, vUv).rgb * strength;
    c *= exposure;
    float luma = dot(c, vec3(0.2126, 0.7152, 0.0722));
    c = max(mix(vec3(luma), c, saturation), 0.0);
    c = toSRGB(c);
    c = (c - 0.5) * contrast + 0.5;
    c = c * gain + lift * (1.0 - c);
    vec2 d = (vUv - 0.5) * vec2(aspect, 1.0);
    float edge = smoothstep(0.5 - softness, 0.5 + softness * 0.5, length(d) / max(1.0, aspect) * 1.4);
    c *= 1.0 - vignette * edge;
    gl_FragColor = vec4(clamp(c, 0.0, 1.0), 1.0);
  }
`;

const LEVELS_MAX = 6;
/**
 * Multisampling on the scene target. Four was the first choice and measured
 * a 13 ms synced median on the platform three runs out of three against 12
 * for two (and for none) — over the line step 98 was given. Two still
 * smooths every edge at the device pixel ratios a screen this game is
 * played on has (gotcha 295).
 */
const SAMPLES = 2;

export function createPost(renderer) {
  const triangle = new THREE.BufferGeometry();
  triangle.setAttribute('position', new THREE.Float32BufferAttribute([-1, -1, 0, 3, -1, 0, -1, 3, 0], 3));
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const quad = new THREE.Mesh(triangle);
  quad.frustumCulled = false;

  const pass = (fragmentShader, uniforms, extra = {}) =>
    new THREE.ShaderMaterial({ vertexShader: fullscreen, fragmentShader, uniforms, depthTest: false, depthWrite: false, ...extra });
  const bright = pass(brightShader, {
    source: { value: null },
    texel: { value: new THREE.Vector2() },
    threshold: { value: 1 },
    knee: { value: 0.5 },
  });
  const down = pass(downShader, { source: { value: null }, texel: { value: new THREE.Vector2() } });
  const up = pass(
    upShader,
    { source: { value: null }, texel: { value: new THREE.Vector2() }, weight: { value: 1 } },
    { blending: THREE.AdditiveBlending, transparent: true }
  );
  const final = pass(finalShader, {
    scene: { value: null },
    bloom: { value: null },
    strength: { value: 0 },
    exposure: { value: 1 },
    saturation: { value: 1 },
    contrast: { value: 1 },
    lift: { value: new THREE.Vector3() },
    gain: { value: new THREE.Vector3(1, 1, 1) },
    vignette: { value: 0 },
    softness: { value: 0.4 },
    aspect: { value: 1 },
  });

  const target = (options = {}) =>
    new THREE.WebGLRenderTarget(1, 1, {
      type: THREE.HalfFloatType,
      depthBuffer: false,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      ...options,
    });
  const sceneTarget = target({ depthBuffer: true, samples: SAMPLES });
  const levels = Array.from({ length: LEVELS_MAX }, () => target());

  let look = null;
  const size = new THREE.Vector2();
  const known = new THREE.Vector2();

  function fit() {
    renderer.getDrawingBufferSize(size);
    if (size.equals(known)) return;
    known.copy(size);
    sceneTarget.setSize(size.x, size.y);
    let w = Math.max(1, Math.round(size.x / 4));
    let h = Math.max(1, Math.round(size.y / 4));
    for (const level of levels) {
      level.setSize(w, h);
      w = Math.max(1, Math.round(w / 2));
      h = Math.max(1, Math.round(h / 2));
    }
  }

  function draw(material, into) {
    quad.material = material;
    renderer.setRenderTarget(into);
    renderer.render(quad, camera);
  }

  return {
    /** The arena's `look`, resolved against the defaults; null draws straight to the canvas. */
    configure(next) {
      look = next?.post ? resolveLook(next.post) : null;
    },
    get active() {
      return Boolean(look);
    },
    get look() {
      return look;
    },

    render(scene, view) {
      if (!look) {
        renderer.info.autoReset = true;
        renderer.setRenderTarget(null);
        renderer.render(scene, view);
        return;
      }
      fit();
      // The counters a tool reads (`renderer.info`) are the scene's, not
      // whichever full-screen pass happened to be drawn last.
      renderer.info.autoReset = false;
      renderer.info.reset();
      renderer.setRenderTarget(sceneTarget);
      renderer.render(scene, view);
      const calls = renderer.info.render.calls;
      const triangles = renderer.info.render.triangles;

      const { bloom, grade, vignette } = look;
      const count = Math.min(LEVELS_MAX, bloom.levels);
      if (bloom.strength > 0 && count > 0) {
        bright.uniforms.source.value = sceneTarget.texture;
        bright.uniforms.texel.value.set(2 / size.x, 2 / size.y);
        bright.uniforms.threshold.value = bloom.threshold;
        bright.uniforms.knee.value = bloom.knee;
        draw(bright, levels[0]);
        for (let i = 1; i < count; i++) {
          down.uniforms.source.value = levels[i - 1].texture;
          down.uniforms.texel.value.set(1 / levels[i - 1].width, 1 / levels[i - 1].height);
          draw(down, levels[i]);
        }
        for (let i = count - 2; i >= 0; i--) {
          up.uniforms.source.value = levels[i + 1].texture;
          up.uniforms.texel.value.set(0.5 / levels[i + 1].width, 0.5 / levels[i + 1].height);
          up.uniforms.weight.value = bloom.spread;
          draw(up, levels[i]);
        }
      }

      const u = final.uniforms;
      u.scene.value = sceneTarget.texture;
      u.bloom.value = levels[0].texture;
      u.strength.value = count > 0 ? bloom.strength : 0;
      u.exposure.value = grade.exposure;
      u.saturation.value = grade.saturation;
      u.contrast.value = grade.contrast;
      u.lift.value.fromArray(grade.lift);
      u.gain.value.fromArray(grade.gain);
      u.vignette.value = vignette.amount;
      u.softness.value = vignette.softness;
      u.aspect.value = size.x / size.y;
      draw(final, null);
      renderer.info.render.calls = calls;
      renderer.info.render.triangles = triangles;
    },

    dispose() {
      sceneTarget.dispose();
      for (const level of levels) level.dispose();
      for (const material of [bright, down, up, final]) material.dispose();
      triangle.dispose();
    },
  };
}

export { LOOK_DEFAULTS };
